import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  FaUserSecret, 
  FaBuilding, 
  FaGlobe, 
  FaSearch, 
  FaFileAlt, 
  FaShieldAlt,
  FaCheckCircle,
  FaArrowRight,
  FaStar,
  FaClock,
  FaChartLine,
  FaPhoneAlt,
  FaEnvelope,
  FaWhatsapp,
  FaHeart,
  FaTrophy,
  FaHandshake
} from "react-icons/fa";
import "../styles/ServicesPage.css";
import Navbar from "../component/Navbar";
import Footer from "../component/Footer";
import ServicesHeroSection from "../component/ServicesHeroSection";

// Service Images
import preMarriageImg from "../assets/w1.png";
import postMarriageImg from "../assets/w2.png";
import cheatingSpouseImg from "../assets/s2.png";
import loveAffairImg from "../assets/w31.png";
import divorceImg from "../assets/w4.png";
import employeeVerificationImg from "../assets/c1.png";
import employeeIntegrityImg from "../assets/a3.png";
import corporateDueImg from "../assets/c2.png";
import financialFraudImg from "../assets/a5.png";
import insiderThreatsImg from "../assets/a6.png";
import startupFraudImg from "../assets/a7.png";
import nriMatrimonialImg from "../assets/n1.png";
import nriBackgroundImg from "../assets/n2.png";
import nriPropertyImg from "../assets/n3.png";
import nriFraudImg from "../assets/n4.png";
import nriLegalImg from "../assets/n5.png";
import nriDueImg from "../assets/n6.png";
import nriAssetImg from "../assets/n7.png";
import nriDocumentImg from "../assets/n8.png";
import AchievementsStrip from "../component/AchievementsStrip";
import SocialStrip from "../component/SocialStrip";

const ServicesPage = () => {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const offset = 120;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 300);
    }
  }, [location]);

  const categories = [
    { id: "all", name: "All Services", icon: <FaShieldAlt />, color: "#3b82f6" },
    { id: "personal", name: "Personal Investigation", icon: <FaUserSecret />, color: "#06d881" },
    { id: "corporate", name: "Corporate Investigation", icon: <FaBuilding />, color: "#f59e0b" },
    { id: "nri", name: "NRI Services", icon: <FaGlobe />, color: "#ef4444" },
  ];

  // Personal Investigation Services
  const personalServices = [
    { 
      id: "pre-marriage-verification",
      name: "Pre-Marriage Verification", 
      description: "Complete background check of prospective spouses including family, career, and character verification.",
      longDescription: "Before tying the knot, it's crucial to know who you're marrying. Our pre-marriage verification service provides a complete background check of your prospective spouse, including family background, career history, financial status, character assessment, and social reputation.",
      icon: <FaUserSecret />,
      image: preMarriageImg,
      features: ["Background Check", "Family Verification", "Career Check", "Character Assessment", "Financial Status"],
      benefits: ["Peace of mind before marriage", "Avoid future complications", "Know your partner better"],
      popular: true,
      color: "#3b82f6"
    },
    { 
      id: "post-marriage-investigation",
      name: "Post-Marriage Investigation", 
      description: "Investigation services for post-marriage issues and concerns with professional approach.",
      longDescription: "Marriage can face challenges. Our post-marriage investigation services help identify and resolve marital issues through professional, discreet investigation.",
      icon: <FaSearch />,
      image: postMarriageImg,
      features: ["Marital Issues", "Compatibility Check", "Family Disputes", "Behavior Analysis"],
      benefits: ["Resolve marital conflicts", "Professional mediation support", "Clarity and peace of mind"],
      popular: false,
      color: "#06d881"
    },
    { 
      id: "cheating-spouse-investigation",
      name: "Cheating Spouse Investigation", 
      description: "Discreet surveillance and evidence collection for suspected infidelity cases.",
      longDescription: "Suspected infidelity can be emotionally devastating. Our discreet surveillance and evidence collection services help you uncover the truth using advanced techniques.",
      icon: <FaSearch />,
      image: cheatingSpouseImg,
      features: ["Surveillance", "Evidence Collection", "Digital Forensics", "Photographic Proof"],
      benefits: ["Get clarity on suspicions", "Legal evidence if needed", "Peace of mind"],
      popular: true,
      color: "#f59e0b"
    },
    { 
      id: "love-affair-investigation",
      name: "Love Affair Investigation", 
      description: "Professional investigation into suspected love affairs and relationships.",
      longDescription: "When you suspect a love affair, you need facts, not assumptions. Our professional investigators conduct thorough, discreet investigations.",
      icon: <FaHeart />,
      image: loveAffairImg,
      features: ["Relationship Check", "Surveillance", "Evidence Gathering", "Pattern Analysis"],
      benefits: ["Verify relationship status", "Gather concrete evidence", "Make informed decisions"],
      popular: false,
      color: "#ef4444"
    },
    { 
      id: "divorce-investigation",
      name: "Divorce Investigation", 
      description: "Evidence collection and documentation for divorce proceedings.",
      longDescription: "Divorce proceedings require solid evidence. Our divorce investigation services help you gather admissible evidence including asset tracing and financial records.",
      icon: <FaFileAlt />,
      image: divorceImg,
      features: ["Evidence Collection", "Legal Documentation", "Witness Statements", "Asset Tracing"],
      benefits: ["Strong legal case", "Fair settlement", "Professional documentation"],
      popular: false,
      color: "#8b5cf6"
    },
  ];

  // Corporate Investigation Services
  const corporateServices = [
    { 
      id: "employee-verification",
      name: "Employee Verification", 
      description: "Comprehensive background verification of employees and potential hires.",
      longDescription: "Your employees are your greatest asset. Our comprehensive employee verification service ensures you hire trustworthy, qualified candidates.",
      icon: <FaBuilding />,
      image: employeeVerificationImg,
      features: ["Background Check", "Education Verification", "Employment History", "Criminal Record Check"],
      benefits: ["Hire trustworthy employees", "Reduce hiring risks", "Protect company reputation"],
      popular: true,
      color: "#3b82f6"
    },
    { 
      id: "employee-integrity",
      name: "Employee Integrity", 
      description: "Monitoring and verification of employee integrity and conduct.",
      longDescription: "Maintain workplace integrity with our employee monitoring services. We help identify potential integrity issues and prevent internal fraud.",
      icon: <FaShieldAlt />,
      image: employeeIntegrityImg,
      features: ["Integrity Check", "Conduct Monitoring", "Background Verification", "Performance Analysis"],
      benefits: ["Maintain workplace integrity", "Prevent internal fraud", "Build trust"],
      popular: false,
      color: "#06d881"
    },
    { 
      id: "corporate-due-diligence",
      name: "Corporate Due Diligence", 
      description: "Complete due diligence for business partnerships and investments.",
      longDescription: "Before entering business partnerships or making investments, conduct thorough due diligence to make informed decisions.",
      icon: <FaBuilding />,
      image: corporateDueImg,
      features: ["Partner Verification", "Financial Check", "Risk Assessment", "Legal Compliance"],
      benefits: ["Secure business partnerships", "Identify potential risks", "Protect investments"],
      popular: true,
      color: "#f59e0b"
    },
    { 
      id: "financial-fraud",
      name: "Financial Fraud", 
      description: "Investigation of financial fraud and embezzlement cases.",
      longDescription: "Financial fraud can devastate your business. Our specialized investigators uncover financial irregularities and gather evidence.",
      icon: <FaChartLine />,
      image: financialFraudImg,
      features: ["Fraud Detection", "Financial Audit", "Evidence Collection", "Transaction Analysis"],
      benefits: ["Recover lost assets", "Legal evidence for prosecution", "Prevent future fraud"],
      popular: true,
      color: "#ef4444"
    },
    { 
      id: "insider-threats",
      name: "Insider Threats", 
      description: "Detection and prevention of insider threats and data breaches.",
      longDescription: "Insider threats are a growing concern. Our security experts help detect and prevent internal security risks.",
      icon: <FaShieldAlt />,
      image: insiderThreatsImg,
      features: ["Threat Detection", "Security Audit", "Risk Mitigation", "Access Monitoring"],
      benefits: ["Prevent data breaches", "Identify security gaps", "Protect sensitive information"],
      popular: false,
      color: "#8b5cf6"
    },
    { 
      id: "startup-fraud",
      name: "Startup Fraud", 
      description: "Investigation services for startup-related fraud and misrepresentation.",
      longDescription: "Before investing in startups, verify their claims. Our startup fraud investigation helps investors verify credentials.",
      icon: <FaBuilding />,
      image: startupFraudImg,
      features: ["Founder Verification", "Financial Audit", "Legal Compliance", "Business Claims"],
      benefits: ["Secure investments", "Verify business claims", "Prevent startup fraud"],
      popular: false,
      color: "#ec4899"
    },
  ];

  // NRI Services
  const nriServices = [
    { 
      id: "nri-matrimonial-investigation",
      name: "NRI Matrimonial Investigation", 
      description: "Specialized investigation services for NRIs facing matrimonial issues.",
      longDescription: "For NRIs seeking matrimonial alliances in India, our specialized investigation services provide comprehensive background checks and legal support.",
      icon: <FaGlobe />,
      image: nriMatrimonialImg,
      features: ["Matrimonial Check", "Background Verification", "Legal Support", "Family Background"],
      benefits: ["Safe matrimonial decisions", "Avoid fraud marriages", "Legal protection"],
      popular: true,
      color: "#3b82f6"
    },
    { 
      id: "nri-background-verification",
      name: "NRI Background Verification", 
      description: "Complete background verification for NRIs and their families remotely.",
      longDescription: "Remote verification made easy. Our NRI background verification services help verify credentials from anywhere in the world.",
      icon: <FaShieldAlt />,
      image: nriBackgroundImg,
      features: ["Family Check", "Property Verification", "Financial Status", "Employment Verification"],
      benefits: ["Remote verification", "Peace of mind", "Secure transactions"],
      popular: false,
      color: "#06d881"
    },
    { 
      id: "nri-property-disputes",
      name: "NRI Property Disputes", 
      description: "Investigation and resolution of property disputes for NRIs.",
      longDescription: "Protect your Indian property rights from abroad. Our investigators help resolve property disputes through title verification.",
      icon: <FaFileAlt />,
      image: nriPropertyImg,
      features: ["Title Verification", "Legal Documentation", "Evidence Collection", "Property Tracing"],
      benefits: ["Protect property rights", "Legal evidence gathering", "Remote investigation"],
      popular: false,
      color: "#f59e0b"
    },
    { 
      id: "nri-fraud-investigation",
      name: "NRI Fraud Investigation", 
      description: "Investigation of fraud cases involving NRIs.",
      longDescription: "Victim of fraud as an NRI? Our specialized fraud investigation services help uncover fraudulent activities and gather evidence.",
      icon: <FaSearch />,
      image: nriFraudImg,
      features: ["Fraud Detection", "Evidence Gathering", "Legal Support", "Transaction Analysis"],
      benefits: ["Recover lost assets", "Legal prosecution support", "Fraud prevention"],
      popular: false,
      color: "#ef4444"
    },
    { 
      id: "nri-legal-support",
      name: "NRI Legal Support", 
      description: "Legal support and documentation assistance for NRIs.",
      longDescription: "Navigate Indian legal matters remotely. Our legal support services provide documentation assistance and court representation.",
      icon: <FaShieldAlt />,
      image: nriLegalImg,
      features: ["Legal Advice", "Documentation", "Court Representation", "Case Management"],
      benefits: ["Expert legal guidance", "Documentation support", "Remote legal assistance"],
      popular: false,
      color: "#8b5cf6"
    },
    { 
      id: "nri-due-diligence",
      name: "NRI Due Diligence", 
      description: "Complete due diligence for NRI business and personal matters.",
      longDescription: "Make informed decisions with our comprehensive due diligence services for NRIs.",
      icon: <FaBuilding />,
      image: nriDueImg,
      features: ["Business Check", "Financial Audit", "Risk Assessment", "Partner Verification"],
      benefits: ["Secure investments", "Risk assessment", "Informed decisions"],
      popular: false,
      color: "#ec4899"
    },
    { 
      id: "nri-asset-tracing",
      name: "NRI Asset Tracing", 
      description: "Tracing and verification of assets for NRIs remotely.",
      longDescription: "Locate and verify assets in India remotely. Our asset tracing services help NRIs identify property ownership.",
      icon: <FaSearch />,
      image: nriAssetImg,
      features: ["Asset Location", "Title Verification", "Legal Documentation", "Value Assessment"],
      benefits: ["Asset recovery", "Legal claims", "Property management"],
      popular: false,
      color: "#06d881"
    },
    { 
      id: "nri-document-verification",
      name: "NRI Document Verification", 
      description: "Verification of documents and credentials for NRIs.",
      longDescription: "Ensure document authenticity with our verification services for educational certificates and legal documents.",
      icon: <FaFileAlt />,
      image: nriDocumentImg,
      features: ["Document Check", "Authentication", "Legal Compliance", "Signature Verification"],
      benefits: ["Document authenticity", "Fraud prevention", "Legal compliance"],
      popular: false,
      color: "#3b82f6"
    },
  ];

  const allServices = [
    ...personalServices.map(s => ({ ...s, category: "personal" })),
    ...corporateServices.map(s => ({ ...s, category: "corporate" })),
    ...nriServices.map(s => ({ ...s, category: "nri" })),
  ];

  const filteredServices = allServices.filter(service => {
    const matchesCategory = activeCategory === "all" || service.category === activeCategory;
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const scrollToCategory = (categoryId) => {
    setActiveCategory(categoryId);
    if (categoryId === "all") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const element = document.getElementById(`${categoryId}-services`);
      if (element) {
        const offset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    }
  };

  // Background Particles Component
  const AnimatedBackground = () => (
    <div className="services-premium-bg">
      <div className="bg-gradient-1"></div>
      <div className="bg-gradient-2"></div>
      <div className="bg-gradient-3"></div>
      <div className="bg-particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle"></div>
        ))}
      </div>
    </div>
  );

  const ServiceCard = ({ service, index }) => {
    const isEven = index % 2 === 0;
    
    return (
      <div id={service.id} className="service-dual-card">
        <div className={`dual-card-container ${isEven ? "content-first" : "image-first"}`}>
          {/* Content Card */}
          <div className="content-card">
            <div className="content-card-inner">
              {service.popular && (
                <div className="popular-badge-card">
                  <FaStar /> Most Popular
                </div>
              )}
              <div className="service-icon-dual" style={{ backgroundColor: `${service.color}15`, color: service.color }}>
                {service.icon}
              </div>
              <h2 className="service-dual-title">{service.name}</h2>
              <p className="service-dual-description">{service.longDescription || service.description}</p>
              
              <div className="service-dual-features">
                <h4>Key Features:</h4>
                <div className="features-dual-list">
                  {service.features.map((feature, idx) => (
                    <span key={idx}><FaCheckCircle /> {feature}</span>
                  ))}
                </div>
              </div>
              
              <div className="service-dual-benefits">
                <h4>Benefits:</h4>
                <div className="benefits-dual-list">
                  {service.benefits.map((benefit, idx) => (
                    <span key={idx}><FaStar /> {benefit}</span>
                  ))}
                </div>
              </div>
              
              <div className="service-dual-buttons">
                <Link to="/contact-us" className="dual-btn primary">
                  Get Started <FaArrowRight />
                </Link>
                <a href="tel:+917428203483" className="dual-btn secondary">
                  Free Consultation
                </a>
              </div>
            </div>
          </div>
          
          {/* Image Card */}
          <div className="image-card">
            <div className="image-card-inner">
              <img src={service.image} alt={service.name} />
              <div className="image-card-overlay"></div>
              <div className="image-card-content">
                <div className="image-badge">
                  <FaStar /> Trusted Service
                </div>
                <div className="image-stats">
                  <div className="image-stat">
                    <span className="image-stat-number">5000+</span>
                    <span className="image-stat-label">Cases</span>
                  </div>
                  <div className="image-stat">
                    <span className="image-stat-number">98%</span>
                    <span className="image-stat-label">Success</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <div className="services-page-complete">
        {/* Hero Section - Imported Component */}
        <ServicesHeroSection />

        {/* Navigation Tabs */}
        <section className="services-nav-section">
          <div className="services-container">
            <div className="category-nav">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`category-nav-btn ${activeCategory === category.id ? "active" : ""}`}
                  onClick={() => scrollToCategory(category.id)}
                  style={{ 
                    borderColor: activeCategory === category.id ? category.color : "rgba(255,255,255,0.2)",
                    background: activeCategory === category.id ? category.color : "rgba(255,255,255,0.05)",
                    color: activeCategory === category.id ? "white" : "#cbd5f5"
                  }}
                >
                  <span className="nav-icon">{category.icon}</span>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
            <div className="search-wrapper">
              <input
                type="text"
                placeholder="🔍 Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-large"
              />
            </div>
          </div>
        </section>

        {/* Search Results */}
        {searchTerm && (
          <section className="search-results-section">
            <div className="services-container">
              <h2 className="section-title">Search Results for "{searchTerm}"</h2>
              {filteredServices.length === 0 ? (
                <div className="no-results">
                  <h3>No services found</h3>
                  <p>Try searching with different keywords</p>
                </div>
              ) : (
                <div className="services-grid-page">
                  {filteredServices.map((service, index) => (
                    <div key={index} className="service-page-card">
                      {service.popular && <div className="service-popular-badge"><FaStar /> Most Popular</div>}
                      <div className="service-card-icon" style={{ backgroundColor: `${service.color}10`, color: service.color }}>
                        {service.icon}
                      </div>
                      <h3 className="service-card-title">{service.name}</h3>
                      <p className="service-card-desc">{service.description.substring(0, 100)}...</p>
                      <button className="service-card-link" onClick={() => {
                        const element = document.getElementById(service.id);
                        if (element) {
                          const offset = 120;
                          const elementPosition = element.getBoundingClientRect().top;
                          const offsetPosition = elementPosition + window.pageYOffset - offset;
                          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
                        }
                      }}>
                        <span>View Details</span>
                        <FaArrowRight />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Personal Investigation Services Section with Animated Background */}
        <section id="personal-services" className="service-category-section">
          <AnimatedBackground />
          <div className="services-container">
            <div className="category-header">
              <div className="category-icon-large" style={{ backgroundColor: "rgba(59, 130, 246, 0.15)" }}>
                <FaUserSecret style={{ color: "#3b82f6" }} />
              </div>
              <div>
                <h2 className="category-title">Personal Investigation Services</h2>
                <p className="category-subtitle">Discreet, confidential, and professional personal investigation services</p>
              </div>
            </div>
            <div className="services-dual-grid">
              {personalServices.map((service, index) => (
                <ServiceCard key={service.id} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Corporate Investigation Services Section with Animated Background */}
        <section id="corporate-services" className="service-category-section">
          <AnimatedBackground />
          <div className="services-container">
            <div className="category-header">
              <div className="category-icon-large" style={{ backgroundColor: "rgba(6, 216, 129, 0.15)" }}>
                <FaBuilding style={{ color: "#06d881" }} />
              </div>
              <div>
                <h2 className="category-title">Corporate Investigation Services</h2>
                <p className="category-subtitle">Protect your business with our comprehensive corporate investigation services</p>
              </div>
            </div>
            <div className="services-dual-grid">
              {corporateServices.map((service, index) => (
                <ServiceCard key={service.id} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* NRI Services Section with Animated Background */}
        <section id="nri-services" className="service-category-section">
          <AnimatedBackground />
          <div className="services-container">
            <div className="category-header">
              <div className="category-icon-large" style={{ backgroundColor: "rgba(245, 158, 11, 0.15)" }}>
                <FaGlobe style={{ color: "#f59e0b" }} />
              </div>
              <div>
                <h2 className="category-title">NRI Services</h2>
                <p className="category-subtitle">Specialized investigation services for Non-Resident Indians</p>
              </div>
            </div>
            <div className="services-dual-grid">
              {nriServices.map((service, index) => (
                <ServiceCard key={service.id} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>
<AchievementsStrip/>
        {/* CTA Section */}
        <section className="services-cta-section">
          <div className="services-container">
            <div className="cta-card">
              <div className="cta-content">
                <h2>Need Help Choosing a Service?</h2>
                <p>Our expert team is here to guide you. Get a free consultation today.</p>
                <div className="cta-buttons">
                  <a href="tel:+917428203483" className="cta-phone-btn">
                    <FaPhoneAlt />
                    Call Now: +91 7428203483
                  </a>
                  <a href="https://wa.me/917428203483" className="cta-whatsapp-btn" target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp />
                    WhatsApp Us
                  </a>
                  <Link to="/contact-us" className="cta-contact-btn">
                    <FaEnvelope />
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
        <SocialStrip/>
      </div>
      <Footer />
    </>
  );
};

export default ServicesPage;